import moment from 'moment';
import { CircularProgress } from '@material-ui/core';
import {
  ArrowBack,
  CheckCircleOutline,
  EventAvailableOutlined,
  FlagOutlined,
} from '@material-ui/icons';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from '../../../app/AppContext';
import Alert from '../../../components/common/Alert';
import MeetingSummaryModal from '../../../components/common/MeetingSummaryModal';
import { formatDate } from '../../../utils/format';
import UsersAPI from '../../UserContainer/user-api';
import SessionForm from './sessionForm';
import SessionSummary from './sessionSummary';

const SessionCreationContainer = () => {
  const { state: routeState } = useLocation();
  const state = routeState || {};
  const history = useHistory();
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.user?.role?.type === 'admin';

  const [meetingList, setMeetingList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingSchedules, setIsCheckingSchedules] = useState(false);
  const [bussyDates, setBussyDates] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '' });
  const [meetingSummary, setMeetingSummary] = useState(null);
  const submissionInFlight = useRef(false);
  const scheduleCheckSequence = useRef(0);
  const [session, setSession] = useState({
    mentorId: '',
    pillarId: '',
    menteeName: state.menteeName,
    menteeId: state.menteeId,
    title: '',
    description: '',
    startDay: '',
    startTime: '',
    timesToRepeat: 0,
    daysToRepeat: 0,
    client: state.clientId,
  });

  const handleFormChange = useCallback(() => {
    scheduleCheckSequence.current += 1;
    setIsCheckingSchedules(false);
    setMeetingList([]);
    setBussyDates([]);
  }, []);

  // generates array with future datetimes
  // with format YYYY-MM-DD for input reading
  const generateMeetingList = ({
    repeatingTimes,
    daysOfDifference,
    startDay,
    startTime,
  }) => {
    const meetings = Array.from({ length: repeatingTimes }, (_, index) => {
      const dateMeeting = formatDate(
        moment(startDay).add(+daysOfDifference * index, 'days'),
        'YYYY-MM-DD'
      );
      return { date: dateMeeting, time: startTime };
    });

    return meetings;
  };

  //allows to show a list of meetings
  useEffect(() => {
    let isCurrentRequest = true;
    const checkSequence = ++scheduleCheckSequence.current;

    (async () => {
      if (!session.mentorId) {
        setMeetingList([]);
        setBussyDates([]);
        return;
      }

      setIsCheckingSchedules(true);
      setMeetingList([]);

      try {
        const { data } = await UsersAPI.getBusySchedule(
          session.mentorId,
          session.menteeId
        );

        if (
          !isCurrentRequest ||
          checkSequence !== scheduleCheckSequence.current
        ) {
          return;
        }

        const busyDates = data || [];
        setBussyDates(busyDates);

        const meets = generateMeetingList({
          repeatingTimes: session.timesToRepeat,
          daysOfDifference: session.daysToRepeat,
          startDay: session.startDay,
          startTime: session.startTime,
        });

        //validates session dates proposed
        //and add message in case users are not available
        const confirmedMeets = meets.map((meet) => {
          const time = meet.time > 9 ? `${meet.time}:00` : `0${meet.time}:00`;
          const bussy = busyDates.find((d) =>
            d.date.includes(`${meet.date} ${time}`)
          );

          if (bussy) {
            return { ...meet, msg: bussy.message };
          } else {
            return meet;
          }
        });

        setMeetingList(confirmedMeets);
      } catch (error) {
        if (
          !isCurrentRequest ||
          checkSequence !== scheduleCheckSequence.current
        ) {
          return;
        }

        setAlertMessage({
          type: 'error',
          message:
            error?.data?.message ||
            'No fue posible verificar la disponibilidad de los participantes.',
        });
      } finally {
        if (
          isCurrentRequest &&
          checkSequence === scheduleCheckSequence.current
        ) {
          setIsCheckingSchedules(false);
        }
      }
    })();

    return () => {
      isCurrentRequest = false;
    };
  }, [session]);

  const getCalendar = (meetings) =>
    meetings.map((meet) => {
      const hour = String(meet.time).split(':')[0].padStart(2, '0');
      return `${meet.date} ${hour}:00`;
    });

  const onClick = () => {
    if (submissionInFlight.current || isCheckingSchedules) return;

    const isComplete =
      session.mentorId &&
      session.mentorName &&
      session.menteeId &&
      session.menteeName &&
      session.pillarId &&
      session.pillarName &&
      session.title &&
      session.description &&
      meetingList.length;

    if (!isComplete) {
      setAlertMessage({
        type: 'error',
        message: 'Debes rellenar el formulario con los datos de la reunión',
      });
      setMeetingSummary(null);
      return;
    }

    if (!isAdmin && meetingList.some((meeting) => meeting.msg)) {
      setAlertMessage({
        type: 'error',
        message:
          'Revisa los horarios programados: uno o más participantes no están disponibles.',
      });
      setMeetingSummary(null);
      return;
    }

    const schedules = getCalendar(meetingList);
    const normalizedSchedules = schedules.map((schedule) =>
      moment(schedule, 'YYYY-MM-DD HH:mm', true)
    );
    const scheduleKeys = normalizedSchedules.map((schedule) =>
      schedule.format('YYYY-MM-DD HH:mm')
    );
    const schedulesAreValid = normalizedSchedules.every((schedule) =>
      schedule.isValid()
    );
    const hasDuplicateSchedules =
      new Set(scheduleKeys).size !== scheduleKeys.length;

    if (
      !schedulesAreValid ||
      hasDuplicateSchedules ||
      schedules.length !== Number(session.timesToRepeat)
    ) {
      setAlertMessage({
        type: 'error',
        message:
          'Revisa las fechas programadas: todas deben ser válidas y diferentes.',
      });
      return;
    }

    const totalSessions = schedules.length;
    const frequency =
      totalSessions === 1
        ? 'No se repite'
        : `Cada ${session.daysToRepeat} ${
            Number(session.daysToRepeat) === 1 ? 'día' : 'días'
          }`;

    setAlertMessage({ type: '', message: '' });
    setMeetingSummary({
      mentor: session.mentorName,
      participant: session.menteeName,
      pillar: session.pillarName,
      schedules,
      frequency,
      totalSessions,
      subject: session.title,
      description: session.description,
      guestEmails: [],
      payload: {
        calendar: schedules,
        idMentee: session.menteeId,
        idMentor: session.mentorId,
        pillar: session.pillarId,
        title: session.title,
        description: session.description,
        notify: true,
        clientId: session.client,
        announcementId: state.announcementId,
      },
    });
  };

  const onSubmit = async () => {
    if (submissionInFlight.current || !meetingSummary) return;

    submissionInFlight.current = true;
    setIsSubmitting(true);

    try {
      await UsersAPI.createMassiveSessions(meetingSummary.payload);

      setAlertMessage({
        type: 'success',
        message: 'Reunión creada exitosamente',
      });
      scheduleCheckSequence.current += 1;
      setMeetingList([]);
      setBussyDates([]);
      setMeetingSummary(null);
    } catch (error) {
      setAlertMessage({
        type: 'error',
        message:
          error?.data?.message || 'No fue posible agendar las reuniones.',
      });
      scheduleCheckSequence.current += 1;
      setMeetingList([]);
      setBussyDates([]);
      setMeetingSummary(null);
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (submissionInFlight.current) return;
    if (history.length > 1) history.goBack();
    else history.push('/clientes');
  };

  return (
    <>
      <div className="session-scheduler-page">
        <div className="session-scheduler">
          <button
            className="session-scheduler__back"
            type="button"
            onClick={goBack}
            disabled={isSubmitting}
          >
            <ArrowBack aria-hidden="true" /> Volver
          </button>
          <header className="session-scheduler__hero">
            <span className="session-scheduler__hero-icon" aria-hidden="true">
              <EventAvailableOutlined />
            </span>
            <div className="session-scheduler__hero-copy">
              <span className="session-scheduler__eyebrow">Gestión de mentorías</span>
              <h1>Crear nueva reunión</h1>
              <p>Selecciona a los participantes, define el contenido y revisa los horarios antes de confirmar.</p>
            </div>
            {state.announcementId && (
              <span className="session-scheduler__context">
                <FlagOutlined aria-hidden="true" />
                Convocatoria #{state.announcementId}
              </span>
            )}
          </header>
          {!state.menteeId ? (
            <div className="session-scheduler__card" role="alert">
              <h2>Selecciona un participante</h2>
              <p>Ingresa desde el detalle de la convocatoria y elige «Agendar reunión» en la pestaña Mentees.</p>
            </div>
          ) : (
            <>
            {alertMessage.message && (
              <Alert type={alertMessage.type}>{alertMessage.message}</Alert>
            )}
            <div className="session-scheduler__layout">
              <SessionForm
                session={session}
                setSession={setSession}
                onFormChange={handleFormChange}
                isBusy={isSubmitting}
                isCheckingSchedules={isCheckingSchedules}
              />
              <SessionSummary
                meetingList={meetingList}
                setMeetingList={setMeetingList}
                bussyDates={bussyDates}
                isCheckingSchedules={isCheckingSchedules}
                isBusy={isSubmitting}
              />
            </div>
            <footer className="session-scheduler__footer">
              <p className="session-scheduler__footer-hint" role="status">
                {isCheckingSchedules
                  ? 'Consultando disponibilidad de los participantes…'
                  : meetingList.length
                  ? `${meetingList.length} ${meetingList.length === 1 ? 'sesión lista' : 'sesiones listas'} para revisar antes de confirmar.`
                  : 'Primero genera los horarios para continuar.'}
              </p>
              <div className="session-scheduler__footer-actions">
                <button
                  type="button"
                  className="session-scheduler__button session-scheduler__button--secondary"
                  disabled={isSubmitting}
                  onClick={goBack}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="session-scheduler__button session-scheduler__button--primary"
                  disabled={isSubmitting || isCheckingSchedules || !meetingList.length}
                  onClick={onClick}
                >
                  {isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutline aria-hidden="true" />}
                  {isSubmitting ? 'Creando reuniones…' : 'Confirmar reuniones'}
                </button>
              </div>
            </footer>
            </>
          )}
        </div>
      </div>
      <MeetingSummaryModal
        open={Boolean(meetingSummary)}
        onCancel={() => {
          if (!submissionInFlight.current) setMeetingSummary(null);
        }}
        onAccept={onSubmit}
        isSubmitting={isSubmitting}
        mentor={meetingSummary?.mentor}
        participant={meetingSummary?.participant}
        pillar={meetingSummary?.pillar}
        schedules={meetingSummary?.schedules || []}
        frequency={meetingSummary?.frequency}
        totalSessions={meetingSummary?.totalSessions}
        subject={meetingSummary?.subject}
        description={meetingSummary?.description}
        guestEmails={meetingSummary?.guestEmails || []}
      />
    </>
  );
};

export default SessionCreationContainer;
